import { useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Divider from '@mui/material/Divider'
import PageBanner from '../components/common/PageBanner'
import Container from '../components/common/Container'
import SectionIntro from '../components/common/SectionIntro'
import ContentBlock from '../components/common/ContentBlock'
import CallToAction from '../components/common/CallToAction'
import { cdnImage } from '../config/env'

const envBenefits = [
  { heading: 'Improved Soil Organic Matter', body: 'Regenerative practices consistently build soil organic matter, increasing nutrient availability, microbial activity, and long-term fertility without synthetic inputs.' },
  { heading: 'Enhanced Water-Holding Capacity', body: 'Soils with higher organic matter and improved structure hold more water, reducing irrigation demand and building drought tolerance across seasons.' },
  { heading: 'Erosion Reduction', body: 'Cover crops and reduced tillage protect exposed soil from wind and water erosion, improving soil structure and preserving topsoil accumulated over millennia.' },
  { heading: 'Improved Infiltration', body: 'Healthy soils absorb rainfall more rapidly, reducing surface run-off, improving groundwater recharge, and reducing flood risk downstream.' },
  { heading: 'Biodiversity Recovery', body: 'Practices that reduce chemical inputs and restore habitat corridors support the return of soil microbiota (which plays a critical role in nutrient cycling and plant health), insects, birds, and larger fauna.' },
  { heading: 'Climate Mitigation', body: 'Carbon sequestration through improved soil organic matter and regenerative grazing management contributes to climate mitigation alongside farming.' },
]

const healthBenefitsA = [
  { heading: 'Higher Nutrient Density', body: 'Studies show regeneratively farmed produce contains higher levels of key micronutrients such as Vitamin K, E and Bs which are essential for immune function, bone health, metabolism, neurological development, and cardiovascular stability - compared to conventionally grown equivalents. While nutrient levels vary by crop and context, the overall pattern indicates that healthier soils are associated with higher nutrient density in food.' },
  { heading: 'Improved Phytochemical Profiles', body: 'Plants grown in biologically active soils produce higher concentrations of secondary metabolites, including antioxidants and anti-inflammatory compounds. These compounds are linked to lower risk of chronic conditions including cardiovascular disease, certain cancers, and metabolic disorders. They function as natural defence systems within plants and, when consumed, contribute to human health resilience.' },
  { heading: 'Better Fat Profiles in Animal Products', body: 'Pasture-raised livestock demonstrate more favourable omega-3 to omega-6 ratios and higher conjugated linoleic acid (CLA) levels. Omega-3 fatty acids are associated with anti-inflammatory benefits and support cardiovascular and neurological health.' },
]

const healthBenefitsB = [
  { heading: 'Reduced Chemical Exposure', body: 'Transitioning away from synthetic pesticides and herbicides reduces dietary exposure to agrochemical residues for both farmers and consumers. Chronic exposure to certain agricultural chemicals has been associated with increased risk of cancer, reproductive disorders, endocrine disruption, and neurological conditions, particularly among vulnerable populations.' },
  { heading: 'Soil Health and Human Health', body: 'Emerging research links the diversity of soil microbiomes to gut health, with implications for immunity, mental health, and chronic disease prevention. Supporting regenerative agriculture is therefore not only an environmental decision, but also a public health one.' },
]


export default function Benefits() {
  useEffect(() => {
    document.title = 'Benefits — Future of Farm Food Library'
  }, [])

  return (
    <>
      <PageBanner
        overline="The Turning Point"
        headline="Why Regenerative Agriculture Matters"
        subtext="Understanding the environmental, health, and systemic case for transitioning to regenerative practices in South Africa."
      />

      {/* What is regenerative ag */}
      <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: 'background.default' }}>
        <Container>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionIntro
                overline="What is regenerative agriculture?"
                heading="Restorative, not extractive"
              />
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Regenerative agriculture is an approach to food production that prioritises the health and function of living systems, including soil biology, water cycles and biodiversity rather than maximising short-term yield through external inputs.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Unlike conventional agriculture, which is fundamentally extractive, drawing down soil capital, depleting aquifers, and reducing biodiversity over time, regenerative systems aim to leave land in a better condition than they found it. This is not merely sustainable in the sense of maintenance; it is actively restorative.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Key principles include minimising soil disturbance, maintaining soil cover, maximising diversity (of crops, species, and practices), integrating livestock, and keeping living roots in soil year-round. These are applied in context-specific ways: practices that work in the Western Cape may differ from those appropriate to KwaZulu-Natal or the Northern Cape.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Critically, regenerative agriculture is defined by outcomes like improving soil health, reducing runoff, building biomass, rather than by adherence to a fixed set of prescribed techniques. This flexibility makes it particularly suited to South Africa's diverse agroecological zones.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={cdnImage('15-benefits-what-is-regen-roots-sphere.png')}
                alt="Plant roots and soil ecosystem inside a glass sphere"
                loading="eager"
                sx={{
                  width: { xs: 340, md: '100%' },
                  height: { xs: 340, md: 'auto' },
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Divider />

      {/* Environmental benefits */}
      <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: 'rgba(138, 154, 91, 0.1)' }}>
        <Container>
          <SectionIntro
            overline="Environmental benefits"
            heading="Restoring the land at scale"
            body="Regenerative practices address the root causes of environmental degradation in South African agriculture, building resilience against the climate pressures already reshaping the farming landscape."
          />
          <Grid container spacing={3}>
            {envBenefits.map((b) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={b.heading}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    backgroundColor: '#ffffff',
                    border: '1px solid rgba(11, 32, 39, 0.08)',
                    borderRadius: 3,
                    borderTop: '5px solid',
                    borderTopColor: 'primary.main',
                    boxShadow: '0 14px 30px rgba(11, 32, 39, 0.08)',
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.1rem', fontWeight: 700 }}>
                    {b.heading}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {b.body}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Divider />

      {/* Health benefits */}
      <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: 'background.paper' }}>
        <Container>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start" sx={{ mb: { xs: 3, md: 4 } }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <SectionIntro
                overline="Health benefits"
                heading="From soil health to human health"
                body="The connection between soil biology and food quality is increasingly supported by nutritional science. Regeneratively produced food is not just environmentally preferable — it is measurably better for human health."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
              <Box
                component="img"
                src={cdnImage('07-benefits-health-lab-scientist-sphere.png')}
                alt="Scientist working in a lab inside a glass sphere"
                sx={{
                  width: { xs: 260, md: 300 },
                  height: { xs: 260, md: 300 },
                  objectFit: 'cover',
                  display: 'block',
                  flexShrink: 0,
                }}
              />
            </Grid>
          </Grid>
          {/* Three nutrient/food quality items */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {healthBenefitsA.map((b) => (
              <Grid size={{ xs: 12, md: 4 }} key={b.heading}>
                <ContentBlock heading={b.heading} sx={{ mb: 0 }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {b.body}
                  </Typography>
                </ContentBlock>
              </Grid>
            ))}
          </Grid>
          {/* Two chemical/microbiome items — side by side */}
          <Grid container spacing={3}>
            {healthBenefitsB.map((b) => (
              <Grid size={{ xs: 12, md: 6 }} key={b.heading}>
                <ContentBlock heading={b.heading} sx={{ mb: 0 }}>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {b.body}
                  </Typography>
                </ContentBlock>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Divider />


      <CallToAction
        heading="Explore the evidence"
        body="Our research library collects the studies, reports, and analyses that underpin the case for regenerative agriculture in Southern Africa."
        actions={[{ label: 'Go to the Research Library', to: '/research', variant: 'contained' }]}
        variant="mist"
      />
    </>
  )
}
